import React, { useMemo, useState, Component } from 'react';
import { FileDown, Download, ArrowUpRight, ArrowDownRight, User, Ruler, Calculator, Plus, Clock, Wallet, PenTool, BookOpen, MessageSquare, Search, Globe, Book, BoxIcon } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { DjayAvatar } from '../../../mascot';
import { DjayInsightBar } from './DjayInsightBar';
import { MODULES, type ModuleData } from '../../../content';
import { downloadCsv } from '../../../utils/csvExport';
import { getModuleFromQuestionId, getModuleTrendDelta } from '../../../utils/activityHelpers';
import type { FamilyMasteryState } from '../../../mastery/familyMasteryEngine';
import { StudentData } from '../pages/StudentDetailPanel';
import { getStudentInsights } from '../utils/differentiationInsights';
import jsPDF from 'jspdf';
const MODULE_ICONS: Record<string, BoxIcon> = {
  Ruler,
  Calculator,
  Plus,
  Clock,
  Wallet,
  PenTool,
  BookOpen,
  MessageSquare,
  Search,
  Globe,
  Book,
  BoxIcon
};
interface StudentReportCardProps {
  students: (StudentData & {
    avgMastery: number;
    trendDelta: number;
    daysPracticed: number;
    weakestModule: ModuleData | null;
    strongestModule: ModuleData | null;
  })[];
  selectedGroup: number;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  periodDays: number;
}
export function StudentReportCard({
  students,
  selectedGroup,
  getModuleMastery,
  periodDays
}: StudentReportCardProps) {
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const selectedStudent = useMemo(() => {
    return students.find(s => s.id === selectedStudentId) || null;
  }, [students, selectedStudentId]);
  const moduleStats = useMemo(() => {
    if (!selectedStudent) return [];
    const fm = selectedStudent.data.familyMastery || {};
    return MODULES.map(module => {
      const mastery = getModuleMastery(selectedStudent.id, module.id);
      const trend = getModuleTrendDelta(fm, selectedGroup, module.id);
      let questionsPracticed = 0;
      for (const [key, state] of Object.entries(fm)) {
        if (!key.includes(`_g${selectedGroup}`)) continue;
        const baseId = key.replace(/_g\d+$/, '');
        const familyModuleId = getModuleFromQuestionId(baseId);
        if (familyModuleId === module.id) {
          questionsPracticed += (state as FamilyMasteryState).attempts || 0;
        }
      }
      return {
        module,
        mastery,
        trend,
        questionsPracticed
      };
    });
  }, [selectedStudent, selectedGroup, getModuleMastery]);
  const handleExportCsv = () => {
    if (!selectedStudent) return;
    const headers = ['Vak', 'Beheersing (%)', 'Trend (%)', 'Vragen Geoefend'];
    const rows = moduleStats.map(m => {
      return [`"${m.module.title}"`, m.mastery, m.trend, m.questionsPracticed].join(',');
    });
    const insights = getStudentInsights(selectedStudent.data, selectedGroup, 3);
    const insightRows = insights.length > 0 ? ['', 'DIFFERENTIATIE-INZICHT', 'Onderwerp,Beheersing (%),Aanbeveling', ...insights.map(i => `"${i.familyName}",${i.mastery},"${i.recommendation}"`)] : [];
    const csvContent = [`Leerling: ${selectedStudent.name}`, `Periode: Laatste ${periodDays} dagen`, '', headers.join(','), ...rows, ...insightRows].join('\n');
    downloadCsv(csvContent, `rapport_${selectedStudent.name.replace(/\s+/g, '_')}`);
  };
  const handleExportPdf = () => {
    if (!selectedStudent) return;
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    // Colors
    const primaryColor = [79, 70, 229]; // Indigo 600
    const textColor = [15, 23, 42]; // Slate 900
    const lightTextColor = [100, 116, 139]; // Slate 500
    const emeraldColor = [16, 185, 129]; // Emerald 500
    const roseColor = [244, 63, 94]; // Rose 500
    const amberColor = [245, 158, 11]; // Amber 500
    const bgLight = [248, 250, 252]; // Slate 50
    // 1. Header
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Leerlingrapport — Meester Djay', margin, 25);
    const dateStr = new Date().toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Datum: ${dateStr}`, pageWidth - margin, 25, {
      align: 'right'
    });
    let yPos = 55;
    // 2. Student info
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(selectedStudent.name, margin, yPos);
    yPos += 8;
    doc.setFontSize(11);
    doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
    doc.text(`Groep ${selectedGroup}  •  Periode: Laatste ${periodDays} dagen`, margin, yPos);
    yPos += 15;
    // 3. Summary stats box
    const boxWidth = (pageWidth - 2 * margin - 15) / 4;
    const boxHeight = 25;
    const stats = [{
      label: 'Gemiddeld',
      value: `${selectedStudent.avgMastery}%`
    }, {
      label: 'Trend',
      value: `${selectedStudent.trendDelta > 0 ? '+' : ''}${selectedStudent.trendDelta}%`
    }, {
      label: 'Dagen geoefend',
      value: `${selectedStudent.daysPracticed}`
    }, {
      label: 'Vragen totaal',
      value: `${moduleStats.reduce((sum, m) => sum + m.questionsPracticed, 0)}`
    }];
    stats.forEach((stat, index) => {
      const x = margin + index * (boxWidth + 5);
      doc.setFillColor(bgLight[0], bgLight[1], bgLight[2]);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x, yPos, boxWidth, boxHeight, 2, 2, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(stat.label.toUpperCase(), x + boxWidth / 2, yPos + 8, {
        align: 'center'
      });
      doc.setFontSize(14);
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.text(stat.value, x + boxWidth / 2, yPos + 18, {
        align: 'center'
      });
    });
    yPos += boxHeight + 15;
    // 4. Highlights
    const highlightWidth = (pageWidth - 2 * margin - 5) / 2;
    if (selectedStudent.strongestModule) {
      doc.setFillColor(236, 253, 245);
      doc.setDrawColor(167, 243, 208);
      doc.roundedRect(margin, yPos, highlightWidth, 20, 2, 2, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(emeraldColor[0], emeraldColor[1], emeraldColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text('STERKSTE VAK', margin + 5, yPos + 8);
      doc.setFontSize(11);
      doc.setTextColor(6, 78, 59);
      doc.text(selectedStudent.strongestModule.title, margin + 5, yPos + 15);
    }
    if (selectedStudent.weakestModule) {
      const weakX = margin + highlightWidth + 5;
      doc.setFillColor(255, 241, 242);
      doc.setDrawColor(254, 205, 211);
      doc.roundedRect(weakX, yPos, highlightWidth, 20, 2, 2, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(roseColor[0], roseColor[1], roseColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text('AANDACHTSPUNT', weakX + 5, yPos + 8);
      doc.setFontSize(11);
      doc.setTextColor(136, 19, 55);
      doc.text(selectedStudent.weakestModule.title, weakX + 5, yPos + 15);
    }
    yPos += 35;
    // 5. Module table
    doc.setFontSize(14);
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.text('Resultaten per vak', margin, yPos);
    yPos += 10;
    // Table header
    doc.setFillColor(bgLight[0], bgLight[1], bgLight[2]);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
    doc.setFontSize(9);
    doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.text('VAK', margin + 5, yPos + 7);
    doc.text('BEHEERSING', margin + 70, yPos + 7);
    doc.text('TREND', margin + 120, yPos + 7);
    doc.text('VRAGEN', pageWidth - margin - 5, yPos + 7, {
      align: 'right'
    });
    yPos += 10;
    // Table rows
    moduleStats.forEach((stat, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(255, 255, 255);
      } else {
        doc.setFillColor(bgLight[0], bgLight[1], bgLight[2]);
      }
      doc.rect(margin, yPos, pageWidth - 2 * margin, 12, 'F');
      doc.setFontSize(10);
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(stat.module.title, margin + 5, yPos + 8);
      // Mastery color
      let mColor = emeraldColor;
      if (stat.mastery < 40) mColor = roseColor;else if (stat.mastery < 70) mColor = amberColor;
      doc.setTextColor(mColor[0], mColor[1], mColor[2]);
      doc.text(`${stat.mastery}%`, margin + 70, yPos + 8);
      // Trend
      let tColor = lightTextColor;
      let tText = '—';
      if (stat.trend > 0) {
        tColor = emeraldColor;
        tText = `+${stat.trend}%`;
      } else if (stat.trend < 0) {
        tColor = roseColor;
        tText = `${stat.trend}%`;
      }
      doc.setTextColor(tColor[0], tColor[1], tColor[2]);
      doc.text(tText, margin + 120, yPos + 8);
      // Questions
      doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
      doc.setFont('helvetica', 'normal');
      doc.text(`${stat.questionsPracticed}`, pageWidth - margin - 5, yPos + 8, {
        align: 'right'
      });
      yPos += 12;
    });
    // 5b. Differentiatie-inzicht section
    const insights = getStudentInsights(selectedStudent.data, selectedGroup, 3);
    if (insights.length > 0) {
      yPos += 10;
      // Check if we need a new page
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = margin;
      }
      doc.setFontSize(14);
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.text('Differentiatie-inzicht', margin, yPos);
      yPos += 10;
      insights.forEach(insight => {
        // Check page break
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = margin;
        }
        doc.setFillColor(254, 243, 199); // amber-50
        doc.setDrawColor(253, 230, 138); // amber-200
        doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'FD');
        doc.setFontSize(10);
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.setFont('helvetica', 'bold');
        doc.text(insight.familyName, margin + 5, yPos + 8);
        doc.setTextColor(roseColor[0], roseColor[1], roseColor[2]);
        doc.text(`${insight.mastery}%`, pageWidth - margin - 5, yPos + 8, {
          align: 'right'
        });
        doc.setFontSize(9);
        doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
        doc.setFont('helvetica', 'normal');
        // Word wrap the recommendation
        const splitText = doc.splitTextToSize(insight.recommendation, pageWidth - 2 * margin - 10);
        doc.text(splitText, margin + 5, yPos + 14);
        yPos += 25;
      });
    }
    // 6. Footer
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
    doc.setFontSize(8);
    doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2]);
    doc.text(`Gegenereerd door Meester Djay • ${dateStr}`, pageWidth / 2, pageHeight - 12, {
      align: 'center'
    });
    // Save
    const safeName = selectedStudent.name.replace(/\s+/g, '_');
    const isoDate = new Date().toISOString().split('T')[0];
    doc.save(`rapport_${safeName}_${isoDate}.pdf`);
  };
  const studentOptions = students.map(s => ({
    value: s.id,
    label: s.name
  }));
  return <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col" data-id="element-6496">
      <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-3 border border-indigo-100" data-id="element-6497">
        <FileDown className="w-6 h-6" data-id="element-6498" />
      </div>
      <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-6499">
        Per-leerling rapport
      </h2>
      <p className="text-slate-500 mb-5" data-id="element-6500">
        Bekijk en exporteer een gedetailleerd rapport per leerling. Ideaal om
        mee te geven aan ouders tijdens 10-minutengesprekken.
      </p>

      <div className="mb-2" data-id="element-6501">
        <Select options={studentOptions} value={selectedStudentId} onChange={e => setSelectedStudentId(e.target.value)} placeholder="Kies een leerling..." icon={<User className="w-4 h-4" data-id="element-6503" />} data-id="element-6502" />
      </div>

      {selectedStudent ? <div className="mt-4 space-y-6" data-id="element-6504">
          {/* Djay Insight Bar */}
          <DjayInsightBar emotion={selectedStudent.avgMastery >= 70 ? 'proud' : selectedStudent.avgMastery >= 50 ? 'happy' : 'encouraging'} message={<>
                {selectedStudent.name} heeft een gemiddelde beheersing van{' '}
                {selectedStudent.avgMastery}%.
                {selectedStudent.strongestModule ? ` Het sterkste vak is ${selectedStudent.strongestModule.title.toLowerCase()}.` : ''}
                {selectedStudent.weakestModule ? ` Extra oefening is aanbevolen voor ${selectedStudent.weakestModule.title.toLowerCase()}.` : ''}
              </>} data-id="element-6505" />

          {/* Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-6506">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 grid grid-cols-[1fr_80px_60px_60px] md:grid-cols-[1fr_100px_60px_80px] gap-3" data-id="element-6507">
              <div className="text-xs font-bold text-slate-500 uppercase" data-id="element-6508">
                Vak
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase" data-id="element-6509">
                Beheersing
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase text-right" data-id="element-6510">
                Trend
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase text-right" data-id="element-6511">
                Vragen
              </div>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-6512">
              {moduleStats.map(stat => {
            const IconComponent = MODULE_ICONS[stat.module.icon] || Book;
            const colorClass = stat.mastery >= 70 ? 'bg-emerald-500' : stat.mastery >= 40 ? 'bg-amber-500' : 'bg-rose-500';
            const textColorClass = stat.mastery >= 70 ? 'text-emerald-600' : stat.mastery >= 40 ? 'text-amber-600' : 'text-rose-600';
            return <div key={stat.module.id} className="px-4 py-3 grid grid-cols-[1fr_80px_60px_60px] md:grid-cols-[1fr_100px_60px_80px] gap-3 items-center hover:bg-slate-50 transition-colors" data-id="element-6513">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-6514">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 ${stat.module.color}`} data-id="element-6515">
                        <IconComponent className="w-4 h-4" data-id="element-6516" />
                      </div>
                      <div className="font-bold text-sm text-slate-900 truncate" data-id="element-6517">
                        {stat.module.title}
                      </div>
                    </div>

                    <div data-id="element-6518">
                      <div className="flex justify-between items-end mb-1" data-id="element-6519">
                        <span className={`text-xs font-bold ${textColorClass}`} data-id="element-6520">
                          {stat.mastery}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-6521">
                        <div className={`h-full rounded-full ${colorClass}`} style={{
                    width: `${stat.mastery}%`
                  }} data-id="element-6522" />
                      </div>
                    </div>

                    <div className="flex justify-end" data-id="element-6523">
                      {stat.trend > 0 ? <div className="flex items-center text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-xs font-bold" data-id="element-6524">
                          <ArrowUpRight className="w-3 h-3 mr-0.5" data-id="element-6525" />
                          {stat.trend}%
                        </div> : stat.trend < 0 ? <div className="flex items-center text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded text-xs font-bold" data-id="element-6526">
                          <ArrowDownRight className="w-3 h-3 mr-0.5" data-id="element-6527" />
                          {Math.abs(stat.trend)}%
                        </div> : <div className="text-slate-400 text-xs font-medium" data-id="element-6528">
                          —
                        </div>}
                    </div>

                    <div className="text-right text-sm font-medium text-slate-600" data-id="element-6529">
                      {stat.questionsPracticed}
                    </div>
                  </div>;
          })}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4 border-t border-slate-100" data-id="element-6530">
            <Button onClick={handleExportCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-6531">
              <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-6532" />
              <div data-id="element-6533">
                <div className="font-bold text-slate-900" data-id="element-6534">
                  Exporteer als CSV
                </div>
                <div className="text-xs text-slate-500 font-normal" data-id="element-6535">
                  Download dit rapport als spreadsheet
                </div>
              </div>
            </Button>

            <Button onClick={handleExportPdf} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-6536">
              <FileDown className="w-4 h-4 mr-3 text-indigo-500" data-id="element-6537" />
              <div data-id="element-6538">
                <div className="font-bold text-slate-900" data-id="element-6539">Rapport in PDF</div>
                <div className="text-xs text-slate-500 font-normal" data-id="element-6540">
                  Gedetailleerde voortgang per leerling
                </div>
              </div>
            </Button>
          </div>
        </div> : <div className="mt-4 flex flex-col items-center justify-center p-8 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 flex-1" data-id="element-6541">
          <div className="mb-3" data-id="element-6542">
            <DjayAvatar size="md" emotion="pointing" data-id="element-6543" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-6544">
            Selecteer een leerling
          </h3>
          <p className="text-xs text-slate-500 max-w-[220px]" data-id="element-6545">
            Kies een leerling hierboven om het individuele rapport te bekijken
            en te exporteren.
          </p>
        </div>}
    </Card>;
}